import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Copy, Download, FileText } from "lucide-react";

type Source = { label?: string; ref?: string };

interface CanonEntry {
  id: string;
  canon_id: string;
  section: string;
  display_order: number;
  statement: string;
  boundaries: string | null;
  sources: Source[] | null;
}

function formatSources(sources: Source[] | null): string | null {
  if (!sources || sources.length === 0) return null;
  const filtered = sources.filter(
    (s) => s.label && !s.label.toLowerCase().includes("ruling")
  );
  if (filtered.length === 0) return null;
  return filtered.map((s) => `${s.label} (${s.ref || ""})`).join("; ");
}

function formatEntry(entry: CanonEntry): string {
  const lines: string[] = [];
  lines.push(`${entry.canon_id} [${entry.section.toUpperCase()}]`);
  lines.push(entry.statement);
  if (entry.boundaries) {
    lines.push(`Boundaries: ${entry.boundaries}`);
  }
  const sources = formatSources(entry.sources);
  if (sources) {
    lines.push(`Sources: ${sources}`);
  }
  lines.push("");
  return lines.join("\n");
}

export default function CanonExport() {
  const [entries, setEntries] = useState<CanonEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [renderDate] = useState(() => new Date().toLocaleDateString());
  const { toast } = useToast();

  useEffect(() => {
    async function load() {
      const { data, error } = await supabase
        .from("canon_entries")
        .select("id, canon_id, section, display_order, statement, boundaries, sources")
        .order("section", { ascending: true })
        .order("display_order", { ascending: true });

      if (error) {
        toast({
          title: "Could not load canon entries",
          description: error.message,
          variant: "destructive",
        });
      } else {
        setEntries((data as CanonEntry[]) || []);
      }
      setLoading(false);
    }
    load();
  }, [toast]);

  const documentText = useMemo(() => {
    const header = `CANON EXPORT\n${entries.length} entries rendered on ${renderDate}\n\n`;
    return header + entries.map(formatEntry).join("\n");
  }, [entries, renderDate]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(documentText);
      toast({ title: "Copied to clipboard" });
    } catch {
      toast({
        title: "Copy failed",
        description: "Your browser did not allow clipboard access.",
        variant: "destructive",
      });
    }
  };

  const handleDownload = () => {
    const blob = new Blob([documentText], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "canon-export.md";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-72" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Canon Export
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {entries.length} entries rendered on {renderDate}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleCopy} className="gap-2">
            <Copy className="h-4 w-4" />
            Copy all
          </Button>
          <Button variant="outline" size="sm" onClick={handleDownload} className="gap-2">
            <Download className="h-4 w-4" />
            Download as .md
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <pre className="whitespace-pre-wrap font-mono text-sm p-4 sm:p-6 leading-relaxed">
            {documentText}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
